import React, { useCallback, useEffect, useState } from 'react';
import { type PlaceLocation, useGoogleApi } from '@goodfynd/context';
import { GeoAutocomplete } from '@goodfynd/forms';
import { Loading } from '@goodfynd/ui';
import classnames from 'clsx';

import config from '../../config';
import strings from '../../config/strings';
import {
  useLocation,
  useLocationDispatch,
} from '../../context/location-context';
import * as storageUtil from '../../utils/storage-util';

import type { InputGeoSearchProps, InputPage } from './types';

import classes from './useGeoSearch.module.css';

export default function useGeoSearch({
  store = true,
}: InputGeoSearchProps = {}) {
  const { isLoaded: googleApiIsLoaded, isLoading } = useGoogleApi();

  const { busy, position } = useLocation();
  const { setPosition } = useLocationDispatch();
  const [location, setLocation] = useState<PlaceLocation | undefined>();

  useEffect(() => {
    if (position && !location) {
      setLocation({
        address: '',
        coordinates: `${position.latitude},${position.longitude}`,
        latitude: position.latitude,
        longitude: position.longitude,
        state: '',
        stateCode: '',
      });
    }
  }, [position, location]);

  if (busy || isLoading) {
    <Loading type="circle" />;
  }

  const handleLocationSelect = useCallback(
    (location?: PlaceLocation | undefined) => {
      if (!location) {
        return;
      }

      // Set geo search input without making another API call
      const { latitude, longitude } = location;
      setLocation(location);
      setPosition({ latitude, longitude });

      if (store) {
        storageUtil.local.set(config.storageKeys.userLocation, {
          latitude,
          longitude,
          coordinates: `${latitude},${longitude}`,
        });
      }
    },
    [setPosition, setLocation, store]
  );

  const renderInput = useCallback(
    (page: InputPage = 'home', position?: TextFieldPosition) => {
      position = position || (page === 'home' ? 'input-bottom' : 'input-right');
      return (
        <GeoAutocomplete
          lookup
          onChange={handleLocationSelect}
          placeholder={strings.labels.homeGeoPlaceholder}
          value={location}
          className={classnames(classes.pageSearch, {
            [classes[`page-${page}`]]: page,
            [classes[`position-${position}`]]: position,
          })}
        />
      );
    },
    [handleLocationSelect, location]
  );

  return { busy, location, ready: googleApiIsLoaded, renderInput };
}
